import { FilterDropdownProps } from "antd/es/table/interface";
import DropdownSearchFilter from "@lib/components/MiniComponents/TableFilters/DropdownSearchFilter";
import { useMemo } from "react";
import { StyledFlex } from "../Table/index.styled";
import { green, red } from "@ant-design/colors";
import { AppTag } from "@lib/components/AppComponents";
import { useIntl } from "react-intl";
import IntlMessages from "@lib/helpers/IntlMessages";

type IsDisabledFiltersProps = FilterDropdownProps & {
  dataIndex: string;
  apiData?: any[];
};

const IsDisabledFilters: React.FC<IsDisabledFiltersProps> = ({
  confirm,
  setSelectedKeys,
  selectedKeys,
  dataIndex,
  apiData = [],
}) => {
  const { messages } = useIntl();

  // Group apiData by isDisabled and count occurrences
  const groupedCounts = useMemo(() => {
    if (!apiData || !Array.isArray(apiData) || apiData.length === 0) {
      return {};
    }

    const counts: Record<string, number> = {};

    apiData.forEach((item) => {
      const isDisabledValue = item[dataIndex];
      if (isDisabledValue !== undefined && isDisabledValue !== null) {
        const key = String(isDisabledValue);
        counts[key] = (counts[key] || 0) + 1;
      }
    });
    return counts;
  }, [apiData, dataIndex]);

  const filterData = useMemo(() => {
    const enabledCount = groupedCounts["false"] || 0;
    const disabledCount = groupedCounts["true"] || 0;
    return [
      {
        text: (
          <StyledFlex
            style={{ gap: 4, justifyContent: "space-between", width: "100%" }}
          >
            <IntlMessages id="common.enabled" />
            <AppTag
              color={enabledCount > 0 ? green[7] : red[7]}
              style={{ margin: 0 }}
            >
              {enabledCount}
            </AppTag>
          </StyledFlex>
        ),
        label: messages["common.enabled"] as string,
        value: "false",
        count: enabledCount,
      },
      {
        text: (
          <StyledFlex
            style={{ gap: 4, justifyContent: "space-between", width: "100%" }}
          >
            <IntlMessages id="common.disabled" />
            <AppTag
              color={disabledCount > 0 ? green[7] : red[7]}
              style={{ margin: 0 }}
            >
              {disabledCount}
            </AppTag>
          </StyledFlex>
        ),
        label: messages["common.disabled"] as string,
        value: "true",
        count: disabledCount,
      },
    ].sort((a, b) => {
      // First sort by count (count > 0 first)
      if (a.count > 0 && b.count === 0) return -1;
      if (a.count === 0 && b.count > 0) return 1;
      // Then sort alphabetically by label
      return a.label.localeCompare(b.label);
    });
  }, [groupedCounts, messages]);

  const currentValue = useMemo(() => {
    if (!selectedKeys || selectedKeys.length === 0) return undefined;
    return Array.isArray(selectedKeys)
      ? selectedKeys.map((key) => String(key))
      : String(selectedKeys).split(",").filter(Boolean);
  }, [selectedKeys]);

  return (
    <DropdownSearchFilter
      field={dataIndex}
      data={filterData}
      value={currentValue}
      isLoading={false}
      enableSearch={false}
      onFinish={(e) => {
        if (setSelectedKeys) {
          // Keep as string values for setSelectedKeys (Key[] expects string | number)
          setSelectedKeys(e.value ? (e.value as string[]) : []);
        }
        if (confirm) {
          confirm();
        }
      }}
    />
  );
};

export default IsDisabledFilters;
